import axios from "axios";
import { createAuthHeaders } from "../api-client";
import type { PresentationConfig, SlideInfo } from "@/stores/slide-store";
import type { PreviewTestRunSnapshot } from "@optidev/shared-types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

export interface EmailVerificationStatusResponse {
  emailVerified: boolean;
  requiresVerification: boolean;
  replyCount: number;
}

// Entry-based data model types (v7)
export type EntryType = "user" | "assistant" | "system" | "summary";
export type ContentType =
  | "text"
  | "tool_use"
  | "tool_result"
  | "image"
  | "interrupt"
  | "error";

export interface SessionEntry {
  id: string;
  sessionId: string;
  parentEntryId: string | null;
  entryType: EntryType;
  contentType: ContentType;
  entryOrder: number;
  role: string | null;
  content: string | null;
  model: string | null;
  toolUseId: string | null;
  toolName: string | null;
  toolInput: Record<string, unknown> | null;
  toolResult: unknown;
  toolIsError: boolean;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadTokens: number | null;
  costUsd: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface SessionSummary {
  sessionId: string;
  totalEntries: number;
  userEntries: number;
  assistantEntries: number;
  toolUseCount: number;
  errorCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheTokens: number;
  totalCostUsd: string;
}

export interface FailedTool {
  id: string;
  toolUseId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  toolResult: unknown;
  errorMessage: string | null;
  createdAt: string;
}

export interface ResumableTask {
  taskId: string;
  sessionId: string;
  status: string;
  errorCode: string | null;
  errorMessage: string | null;
  currentEntryId: string | null;
  queuedAt: string;
}

export interface ResumeTaskResponse {
  success: boolean;
  task: {
    id: string;

    status: string;
    sessionId: string;
  };
}

// Conversation API types
export interface ConversationActiveTask {
  taskId: string;
  status: string;
  userMessageId: string;
}

export interface ConversationResponse {
  messages: unknown[]; // Message type from agentChatStore
  activeTasks: ConversationActiveTask[];
}

export interface UpdateThemeResponse {
  success: boolean;
  theme: "light" | "dark";
  themeName: string;
  message: string;
}

export const claudeAgentApi = {
  /**
   * Get conversation messages and active tasks for a session
   * Used for loading session history and resuming streaming tasks
   */
  async getConversation(
    token: string,
    sessionId: string,
  ): Promise<ConversationResponse> {
    const response = await axios.get<ConversationResponse>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/conversation`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Reconstruct the Test Window run history for a session from persisted
   * entries — so the Tests tab shows on any browser/device.
   */
  async getPreviewTestRuns(
    token: string,
    sessionId: string,
  ): Promise<{ runs: PreviewTestRunSnapshot[] }> {
    const response = await axios.get<{ runs: PreviewTestRunSnapshot[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/preview-test-runs`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Check email verification status for chat gating
   * Returns whether user needs to verify email before sending more messages
   */
  async checkEmailVerificationStatus(
    token: string,
    workspaceId?: string,
  ): Promise<EmailVerificationStatusResponse> {
    const response = await axios.get<EmailVerificationStatusResponse>(
      `${API_URL}/api/claude-agent/email-verification-status`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: workspaceId ? { workspaceId } : undefined,
      },
    );
    return response.data;
  },

  // === Entry-based Data Model APIs (v7) ===

  /**
   * Get all entries for a session as a tree structure
   * Returns entries with parent-child relationships
   */
  async getEntryTree(
    token: string,
    sessionId: string,
  ): Promise<{ entries: SessionEntry[] }> {
    const response = await axios.get<{ entries: SessionEntry[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/entries`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get session summary with aggregated stats
   * Includes entry counts, token usage, and cost totals
   */
  async getSessionSummary(
    token: string,
    sessionId: string,
  ): Promise<SessionSummary> {
    const response = await axios.get<SessionSummary>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/summary`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Search entries within a session
   * Supports filtering by tool name, file path, and error status
   */
  async searchEntries(
    token: string,
    sessionId: string,
    filters?: {
      tool?: string;
      file?: string;
      errorOnly?: boolean;
    },
  ): Promise<{ entries: SessionEntry[] }> {
    const params = new URLSearchParams();
    if (filters?.tool) params.append("tool", filters.tool);
    if (filters?.file) params.append("file", filters.file);
    if (filters?.errorOnly) params.append("errorOnly", "true");

    const response = await axios.get<{ entries: SessionEntry[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/entries/search?${params.toString()}`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get failed tool executions for a session
   * Returns tool_result entries where toolIsError is true
   */
  async getFailedTools(
    token: string,
    sessionId: string,
  ): Promise<{ failedTools: FailedTool[] }> {
    const response = await axios.get<{ failedTools: FailedTool[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/failed-tools`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get resumable tasks for a session
   * Returns tasks in PAUSED status with isResumable=true
   */
  async getResumableTasks(
    token: string,
    sessionId: string,
  ): Promise<{ tasks: ResumableTask[] }> {
    const response = await axios.get<{ tasks: ResumableTask[] }>(
      `${API_URL}/api/claude-agent/sessions/${sessionId}/resumable-tasks`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Should the session-feedback pulse show right now? Server decides
   * (min-turns since last prompt in this session + per-user cooldown since the
   * last rate/dismiss). Re-askable — no permanent per-session lock.
   */
  async getFeedbackEligibility(
    token: string,
    sessionId: string,
  ): Promise<{ show: boolean; reason?: string; turns?: number }> {
    const response = await axios.get(
      `${API_URL}/api/claude-agent/feedback/eligibility`,
      { params: { sessionId }, headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /** Submit a session rating (+ optional note). Append-only server-side. */
  async submitSessionFeedback(
    token: string,
    input: {
      sessionId: string;
      rating: "excellent" | "good" | "bad" | "many_issues";
      comment?: string;
    },
  ): Promise<{ success: boolean; id: string }> {
    const response = await axios.post(
      `${API_URL}/api/claude-agent/feedback`,
      input,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /** Record a "Not now" dismissal (extends the per-user cooldown). */
  async dismissSessionFeedback(
    token: string,
    sessionId: string,
  ): Promise<{ success: boolean }> {
    const response = await axios.post(
      `${API_URL}/api/claude-agent/feedback/dismiss`,
      { sessionId },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Resume a paused task (for recoverable API errors like 429, 529)
   * Requeues the task for processing
   */
  async resumeTask(token: string, taskId: string): Promise<ResumeTaskResponse> {
    const response = await axios.post<ResumeTaskResponse>(
      `${API_URL}/api/claude-agent/tasks/${taskId}/resume`,
      {},
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Read a file from the dev container via sidecar proxy
   */
  async readFile(
    token: string,
    devContainerId: string,
    filePath: string,
  ): Promise<{ content: string }> {
    const response = await axios.get<{ content: string }>(
      `${API_URL}/api/claude-agent/read-file`,
      {
        params: { devContainerId, filePath },
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Fetch a slide thumbnail JPEG as a Blob, addressed by content-versioned
   * filename (e.g. `t-slide-1-1715234567890.jpg`). The filename comes from
   * `slide.thumbnail` in the slides.json manifest — each capture writes a
   * new filename, so the URL is unique per generation and safe to cache
   * forever.
   * Throws on non-2xx (including 404 — no thumbnail captured yet).
   */
  async fetchSlideThumbnail(
    token: string,
    devContainerId: string,
    filename: string,
    signal?: AbortSignal,
    cacheBust?: number,
  ): Promise<Blob> {
    // The sidecar now writes a stable filename `t-{slide.id}.jpg` and
    // overwrites it on each re-capture; the by-filename endpoint serves
    // `Cache-Control: immutable`, so different bytes under the same URL
    // would still be shadowed by the browser cache. Append a `?v={n}`
    // query — backend ignores it, but the browser treats it as a new
    // resource and skips its cache.
    const cacheBustSuffix =
      typeof cacheBust === "number" && cacheBust > 0 ? `?v=${cacheBust}` : "";
    const res = await fetch(
      `${API_URL}/api/claude-agent/slides/thumbnails/${devContainerId}/file/${encodeURIComponent(filename)}${cacheBustSuffix}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    if (!res.ok) {
      throw new Error(`thumbnail ${filename} fetch failed: ${res.status}`);
    }
    return res.blob();
  },

  /**
   * Fetch a slide thumbnail by slide ID — sidecar reads `t-{slideId}.jpg`
   * directly (with fallback to legacy `t-{slideId}-{captureUuid}.jpg`).
   * URL is stable per slide; the frontend no longer needs to know the
   * filename or read `slide.thumbnail` from the manifest.
   */
  async fetchSlideThumbnailBySlideId(
    token: string,
    devContainerId: string,
    slideId: string,
    signal?: AbortSignal,
    cacheBust?: number,
  ): Promise<Blob> {
    // The by-slide-id endpoint serves `Cache-Control: no-cache` + ETag, so a
    // conditional revalidation happens per fetch — but the URL is stable per
    // slide, so some browsers serve the disk-cached body without hitting the
    // network at all. Appending `?v={n}` forces a fresh request after the
    // slides-thumbnails-updated event bumps revision. Backend ignores the
    // query string.
    const cacheBustSuffix =
      typeof cacheBust === "number" && cacheBust > 0 ? `?v=${cacheBust}` : "";
    const res = await fetch(
      `${API_URL}/api/claude-agent/slides/thumbnails/${devContainerId}/by-slide-id/${encodeURIComponent(slideId)}${cacheBustSuffix}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    if (!res.ok) {
      throw new Error(
        `thumbnail by slide-id ${slideId} fetch failed: ${res.status}`,
      );
    }
    return res.blob();
  },

  /**
   * Reorder slides in slides.json via sidecar
   */
  async reorderSlides(
    token: string,
    devContainerId: string,
    slideOrder: string[],
  ): Promise<{ success: boolean; slides?: unknown[] }> {
    const response = await axios.put<{ success: boolean; slides?: unknown[] }>(
      `${API_URL}/api/claude-agent/slides/reorder`,
      {
        devContainerId,
        slideOrder,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update presentation config in slides.json via sidecar
   */
  async updateSlidesConfig(
    token: string,
    devContainerId: string,
    config: PresentationConfig,
  ): Promise<{ success: boolean; config?: PresentationConfig }> {
    const response = await axios.put<{
      success: boolean;
      config?: PresentationConfig;
    }>(
      `${API_URL}/api/claude-agent/slides/config`,
      {
        devContainerId,
        config,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Delete a slide from slides.json via sidecar.
   * Server refuses to delete the last remaining slide (returns 400).
   */
  async deleteSlide(
    token: string,
    devContainerId: string,
    slideId: string,
  ): Promise<{ success: boolean; slides?: SlideInfo[] }> {
    const response = await axios.delete<{
      success: boolean;
      slides?: SlideInfo[];
    }>(`${API_URL}/api/claude-agent/slides/${slideId}`, {
      headers: createAuthHeaders(token),
      data: { devContainerId },
    });
    return response.data;
  },

  /**
   * Duplicate a slide via sidecar. Server clones the SlideN.tsx file, copies
   * the source's thumbnail under a fresh filename, inserts the new entry into
   * slides.json right after the source, and renumbers positions.
   */
  async duplicateSlide(
    token: string,
    devContainerId: string,
    slideId: string,
  ): Promise<{
    success: boolean;
    slides?: SlideInfo[];
    newSlideId?: string;
  }> {
    const response = await axios.post<{
      success: boolean;
      slides?: SlideInfo[];
      newSlideId?: string;
    }>(
      `${API_URL}/api/claude-agent/slides/${slideId}/duplicate`,
      { devContainerId },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Update per-slide config override in slides.json via sidecar
   */
  async updateSlideConfig(
    token: string,
    devContainerId: string,
    slideId: string,
    config: {
      interval?: number;
      transition?: string;
      transitionDuration?: number;
      // Speaker notes — `null` or empty string clears the field on the
      // sidecar, any other string (≤10KB) sets it.
      speakerNotes?: string | null;
    },
  ): Promise<{ success: boolean }> {
    const response = await axios.put<{ success: boolean }>(
      `${API_URL}/api/claude-agent/slides/${slideId}/config`,
      {
        devContainerId,
        config,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update IDE theme (dark/light) in the dev container
   */
  async updateTheme(
    token: string,
    devContainerId: string,
    theme: "light" | "dark",
  ): Promise<UpdateThemeResponse> {
    const response = await axios.post<UpdateThemeResponse>(
      `${API_URL}/api/claude-agent/update-theme`,
      {
        devContainerId,
        theme,
      },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },
};
